import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { map, retry, tap, catchError } from 'rxjs/operators';

import {
  ApplicationInsights,
  BlobStorageAccess,
  LoginDto,
  UserToken,
} from '@plugins/shared/types/auth';

import { SERVERLESS } from './auth.const';

declare global {
  interface Window {
    __plugin_auth: LoginDto;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly serverless = inject<boolean>(SERVERLESS);

  isLoggedIn(): boolean {
    return !!window.__plugin_auth;
  }

  login(middlewareToken: string): Observable<UserToken> {
    if (this.serverless) {
      const user = jwtDecode<UserToken>(middlewareToken);
      window.__plugin_auth = { user, auth: middlewareToken };
      return of(user);
    }
    return this.httpClient
      .post<LoginDto>('/api/auth/login', { jwt: middlewareToken })
      .pipe(
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status > 499) {
            return throwError(() => err);
          }
          return EMPTY;
        }),
        retry({ delay: 7000 }),
        tap((login) => (window.__plugin_auth = login)),
        map(({ user }) => user),
      );
  }

  handover(token: string) {
    return this.httpClient
      .post<LoginDto>('/api/auth/refresh', { jwt: token })
      .pipe(
        tap((login) => (window.__plugin_auth = login)),
        map(({ user }) => user),
      );
  }

  get authToken(): string {
    return window.__plugin_auth.auth;
  }

  get userToken(): UserToken {
    return window.__plugin_auth.user;
  }

  get instanceId(): string {
    return window.__plugin_auth.user.instance_id;
  }

  get blob(): BlobStorageAccess | undefined {
    return window.__plugin_auth.blob;
  }

  get appInsights(): ApplicationInsights | undefined {
    return window.__plugin_auth.appInsights;
  }
}
